/* Duvarkapla feature funnel — anonymous last-touch attribution for WallView and discovery tools. */
(function () {
  'use strict';

  const ENDPOINT = 'https://wallview-duvarkapla.muralls.workers.dev/api/funnel-event';
  const VISITOR_KEY = 'duvarkapla:funnel:visitor';
  const ATTRIBUTION_KEY = 'duvarkapla:funnel:attribution';
  const ATTRIBUTION_TTL = 30 * 24 * 60 * 60 * 1000;
  const SOURCES = new Set(['wallview', 'visual_search', 'find_similar', 'wallpaper_finder']);
  const FINDER_MODES = new Set(['text', 'room']);
  const pageSessions = new Map();
  const memoryStorage = new Map();
  const storage = (() => {
    try { if (window.localStorage) return window.localStorage; } catch {}
    return {
      getItem: (key) => memoryStorage.get(key) || null,
      setItem: (key, value) => memoryStorage.set(key, String(value)),
      removeItem: (key) => memoryStorage.delete(key),
    };
  })();

  const uuid = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  };

  const readJson = (key) => {
    try { return JSON.parse(storage.getItem(key) || 'null'); } catch { return null; }
  };

  let visitorId = storage.getItem(VISITOR_KEY);
  if (!visitorId) {
    visitorId = uuid();
    try { storage.setItem(VISITOR_KEY, visitorId); } catch {}
  }

  const sessionFor = (source) => {
    if (!SOURCES.has(source)) return '';
    if (!pageSessions.has(source)) pageSessions.set(source, uuid());
    return pageSessions.get(source);
  };

  const normalizedMode = (source, mode) => source === 'wallpaper_finder' && FINDER_MODES.has(mode) ? mode : '';

  const currentAttribution = () => {
    const attribution = readJson(ATTRIBUTION_KEY);
    if (!attribution || !SOURCES.has(attribution.source) || !attribution.sessionId) return null;
    if (Date.now() - Number(attribution.activatedAt || 0) > ATTRIBUTION_TTL) {
      try { storage.removeItem(ATTRIBUTION_KEY); } catch {}
      return null;
    }
    return attribution;
  };

  const send = (source, event, details = {}) => {
    if (!SOURCES.has(source)) return;
    const payload = JSON.stringify({
      source,
      event,
      sessionId: details.sessionId || sessionFor(source),
      visitorId,
      page: `${location.pathname}${location.search}`.slice(0, 500),
      productHandle: String(details.productHandle || '').slice(0, 180),
      productId: String(details.productId || '').slice(0, 80),
      mode: String(details.mode || '').slice(0, 30),
      placement: String(details.placement || '').slice(0, 40),
      at: new Date().toISOString(),
    });
    if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'text/plain' }))) return;
    fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
  };

  const injectAttributionInputs = (attribution) => {
    if (!attribution) return;
    const values = {
      '_funnel_source': attribution.source,
      '_funnel_session': attribution.sessionId,
      '_funnel_visitor': visitorId,
      '_funnel_mode': attribution.mode || '',
    };
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      Object.entries(values).forEach(([key, value]) => {
        let input = form.querySelector(`[data-funnel-property="${key}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.dataset.funnelProperty = key;
          form.appendChild(input);
        }
        input.value = value;
      });
    });
  };

  const activate = (source, sessionId = sessionFor(source), mode = '') => {
    if (!SOURCES.has(source) || !sessionId) return null;
    pageSessions.set(source, sessionId);
    const attribution = { source, sessionId, mode: normalizedMode(source, mode), activatedAt: Date.now() };
    try { storage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution)); } catch {}
    injectAttributionInputs(attribution);
    return attribution;
  };

  const decorateFormData = (formData, attribution) => {
    if (!attribution || !(formData instanceof FormData)) return formData;
    formData.set('properties[_funnel_source]', attribution.source);
    formData.set('properties[_funnel_session]', attribution.sessionId);
    formData.set('properties[_funnel_visitor]', visitorId);
    formData.set('properties[_funnel_mode]', attribution.mode || '');
    return formData;
  };

  const decorateJson = (body, attribution) => {
    if (!attribution || !body || typeof body !== 'object') return body;
    const decorateItem = (item) => {
      if (!item || typeof item !== 'object') return;
      item.properties = { ...(item.properties || {}), _funnel_source: attribution.source, _funnel_session: attribution.sessionId, _funnel_visitor: visitorId, _funnel_mode: attribution.mode || '' };
    };
    if (Array.isArray(body.items)) body.items.forEach(decorateItem);
    else decorateItem(body);
    return body;
  };

  const originalFetch = window.fetch.bind(window);
  window.fetch = async function funnelFetch(input, init = {}) {
    const rawUrl = typeof input === 'string' ? input : input?.url || '';
    const isCartAdd = /\/cart\/add(?:\.js)?(?:\?|$)/.test(rawUrl);
    const attribution = isCartAdd ? currentAttribution() : null;
    let nextInit = init;
    if (attribution && init.body instanceof FormData) {
      decorateFormData(init.body, attribution);
    } else if (attribution && typeof init.body === 'string' && /application\/json/i.test(String(init.headers?.['Content-Type'] || init.headers?.get?.('Content-Type') || ''))) {
      try { nextInit = { ...init, body: JSON.stringify(decorateJson(JSON.parse(init.body), attribution)) }; } catch {}
    }
    const response = await originalFetch(input, nextInit);
    if (attribution && response.ok) {
      send(attribution.source, 'added_to_cart', { mode: attribution.mode || '', sessionId: attribution.sessionId });
    }
    return response;
  };

  const sourceForTrigger = (trigger) => {
    if (trigger.matches('[data-wv-open]')) return 'wallview';
    if (trigger.matches('[data-wallpaper-finder-open]')) return 'wallpaper_finder';
    if (trigger.dataset.visualSearchImage) return 'find_similar';
    if (trigger.matches('[data-visual-search-open]')) return 'visual_search';
    return '';
  };

  const observeTriggers = () => {
    const triggers = [...document.querySelectorAll('[data-wv-open], [data-visual-search-open], [data-wallpaper-finder-open]')];
    if (!triggers.length) return;
    const seen = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const source = sourceForTrigger(entry.target);
        const placement = String(entry.target.dataset.wallpaperFinderPlacement || 'default');
        const key = `${source}:${placement}`;
        if (!source || seen.has(key)) return;
        seen.add(key);
        send(source, 'viewed', { placement });
      });
    }, { threshold: 0.35 });
    triggers.forEach((trigger) => observer.observe(trigger));
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-wv-open], [data-visual-search-open], [data-wallpaper-finder-open]');
    if (!trigger) return;
    const source = sourceForTrigger(trigger);
    const attribution = activate(source);
    if (attribution) send(source, 'opened', {
      sessionId: attribution.sessionId,
      productHandle: trigger.dataset.visualSearchHandle,
      placement: trigger.dataset.wallpaperFinderPlacement || 'default',
    });
  }, true);

  const params = new URLSearchParams(location.search);
  const incomingSource = params.get('_mf_source');
  const incomingSession = params.get('_mf_session');
  const incomingMode = params.get('_mf_mode');
  if (SOURCES.has(incomingSource) && incomingSession) activate(incomingSource, incomingSession, incomingMode);
  else injectAttributionInputs(currentAttribution());

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeTriggers, { once: true });
  else observeTriggers();

  window.DuvarkaplaFunnel = {
    activate,
    send,
    sessionFor,
    current: currentAttribution,
    trackUpload(source, details = {}) { const attribution = activate(source, sessionFor(source), details.mode); send(source, 'uploaded', { ...details, mode: attribution?.mode, sessionId: attribution?.sessionId }); },
    trackSearch(source, details = {}) { const attribution = activate(source, sessionFor(source), details.mode); send(source, 'searched', { ...details, mode: attribution?.mode, sessionId: attribution?.sessionId }); },
    trackProductClick(source, details = {}) {
      const current = currentAttribution();
      const attribution = current?.source === source ? current : activate(source, sessionFor(source), details.mode);
      send(source, 'product_clicked', { ...details, mode: attribution?.mode, sessionId: attribution?.sessionId });
    },
    attributedUrl(url, source) {
      try {
        const current = currentAttribution();
        const attribution = current?.source === source ? current : activate(source);
        const result = new URL(url, location.origin);
        result.searchParams.set('_mf_source', source);
        result.searchParams.set('_mf_session', attribution.sessionId);
        if (attribution.mode) result.searchParams.set('_mf_mode', attribution.mode);
        return result.toString();
      } catch { return url; }
    },
  };
})();
